import { Link } from 'react-router-dom';
import { AlertTriangle, KeyRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { useLicenceEnforcement, type LicenceEnforcement } from '@/api/queries/go-live';

// ─────────────────────────────────────────────────────────────
// Licence banner of an embedded install (AppLayout), for every signed-in
// user: the days left during the grace, the refusal to issue once it is
// over, a warning when the licence cannot be checked, and the day since
// which the state of this SFE version cannot be checked (no verified
// revocation list for 20 days, #622 D). Above it, the issuing suspension of
// a revocation list read older than its newest anchor, whatever the licence.
// The API reports phase AUCUNE and no suspension online, phase AUCUNE with a
// valid licence; errors render nothing.
// ─────────────────────────────────────────────────────────────

function frenchDay(day: string): string {
  return day.split('-').reverse().join('/');
}

function SuspensionBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="mb-4 flex flex-wrap items-start justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800"
    >
      <div className="flex min-w-0 items-start gap-2 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <p className="min-w-0">
          <span className="font-semibold">Émission de factures suspendue</span> — {message}
        </p>
      </div>
    </div>
  );
}

export function LicenceBanner() {
  const { data } = useLicenceEnforcement();
  if (!data) return null;
  const suspension = data.issuingSuspension ?? null;
  return (
    <>
      {suspension !== null && <SuspensionBanner message={suspension.message} />}
      <LicencePhaseBanner data={data} />
    </>
  );
}

function LicencePhaseBanner({ data }: { data: LicenceEnforcement }) {
  const { hasPermission } = usePermissions();

  const unverifiableSince = data.sfeStateUnverifiableSince ?? null;
  if (data.phase === 'AUCUNE' && unverifiableSince === null) return null;

  const licence = data.phase !== 'AUCUNE';
  const blocked = data.phase === 'BLOQUEE';
  const title =
    data.phase === 'GRACE'
      ? `Licence — J-${data.daysLeft ?? 0}`
      : blocked
        ? 'Émission de factures bloquée'
        : data.phase === 'AVERTISSEMENT'
          ? 'Licence non vérifiable'
          : 'Homologation non vérifiable';
  const stale =
    unverifiableSince === null
      ? null
      : `État d'homologation de cette version non vérifiable depuis le ${frenchDay(unverifiableSince)}.`;

  return (
    <div
      role={blocked ? 'alert' : 'status'}
      className={cn(
        'mb-4 flex flex-wrap items-start justify-between gap-2 rounded-xl border px-4 py-3',
        blocked ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'
      )}
    >
      <div className="flex min-w-0 items-start gap-2 text-sm">
        {blocked ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        ) : (
          <KeyRound className="mt-0.5 h-4 w-4 flex-shrink-0" />
        )}
        <p className="min-w-0">
          <span className="font-semibold">{title}</span> — {licence ? data.message : stale}
          {licence && stale !== null && <> {stale}</>}
          {licence && data.phase !== 'AVERTISSEMENT' && !hasPermission('tenant:settings') && (
            <> Contactez votre administrateur.</>
          )}
        </p>
      </div>
      {licence && data.phase !== 'AVERTISSEMENT' && hasPermission('tenant:settings') && (
        <Link
          to="/settings/mise-en-service"
          className="text-sm font-medium underline underline-offset-2 hover:opacity-80"
        >
          Renouveler la licence
        </Link>
      )}
    </div>
  );
}

export default LicenceBanner;
